/**
 * THE CHOSEN-PLAN PAGE, IN PIECES EVERY MODULE SHARES (owner, 22 Oct 2026: "Yes i want this all consistent" — the
 * consistency audit found the village, downsizing, granny flat, cashflow and support at home plan pages each built
 * their own card, figures, buttons and links, and none had aged care's "Model something else").
 *
 * The shape is aged care's (AgedCareHub.tsx):
 *   - ChosenPlanCard: green outline; "Chosen plan" with the date; the plan's words; what is left to the family and the
 *     change against the benchmark; a strip of figures; the module's own body; three buttons (Explain this, What could
 *     go wrong, All the plans we tested →).
 *   - DocumentAndNextStep: the advice document and the next action, one card with two cells.
 *   - ModelSomethingElse: the other models the module can start, each saying what it keeps.
 *   - PlanPageLinks: the ways on, as links with icons (none of them is the next step).
 */
import React from 'react';
import { BookOpen, ShieldAlert, Sparkles, CheckCircle2, FileText } from 'lucide-react';
import { formatCurrency } from '../../utils/index.ts';

const money = (n: number) => formatCurrency(Math.abs(Math.round(n)));

export interface PlanFigure {
    label: string;
    /** The figure, already formatted ("$14,118"), with an optional unit. */
    value: React.ReactNode;
    unit?: string;
    /** The line under it: the benchmark's figure or the change. */
    note?: React.ReactNode;
    /** Green for money left over; a shortfall or a cost stays plain (a cost is a fact, not bad news — aged care's rule). */
    tone?: 'plain' | 'good';
    testId?: string;
}

export interface ChosenPlanCardProps {
    chosenOn?: string | null;
    builtByYou?: boolean;
    title: string;
    sub?: React.ReactNode;
    estate: number;
    years: number;
    /** The change against the benchmark, or null when there is none to compare with. */
    estateDelta?: number | null;
    /** "doing nothing", "staying put", "changing nothing". */
    baseWord: string;
    figures: PlanFigure[];
    children?: React.ReactNode;
    onExplain: () => void;
    onRisks: () => void;
    onAllPlans: () => void;
    testIdPrefix: string;
}

export const ChosenPlanCard = ({ chosenOn, builtByYou = false, title, sub, estate, years, estateDelta = null, baseWord, figures, children, onExplain, onRisks, onAllPlans, testIdPrefix }: ChosenPlanCardProps) => (
    <div className="overflow-hidden rounded-2xl border-2 border-emerald-500 bg-white shadow-card" data-testid={`${testIdPrefix}-card`}>
        <div className="flex flex-wrap items-start justify-between gap-6 px-6 pt-5 pb-4">
            <div className="min-w-[260px] flex-1">
                <span className="inline-flex items-center gap-1.5 text-[12.5px] font-semibold text-emerald-700" data-testid={`${testIdPrefix}-chosen-chip`}><CheckCircle2 size={15} strokeWidth={2} aria-hidden />Chosen plan{chosenOn ? `, ${chosenOn}` : ''}{builtByYou ? ' · built by you' : ''}</span>
                <h2 className="mt-2 text-[22px] font-bold leading-snug tracking-tight text-slate-900">{title}</h2>
                {sub && <p className="mt-1 text-[13px] leading-relaxed text-slate-600">{sub}</p>}
            </div>
            <div className="text-right">
                <p className="text-[36px] font-bold leading-none tabular-nums tracking-tight text-brand-navy">{money(estate)}</p>
                <p className="mt-1 text-[13px] text-slate-600">left to the family after about {years} {years === 1 ? 'year' : 'years'}</p>
                {estateDelta != null && Math.abs(estateDelta) >= 1 && (
                    <p className={`mt-0.5 text-[13px] font-bold tabular-nums ${estateDelta >= 0 ? 'text-emerald-700' : 'text-rose-700'}`}>{estateDelta >= 0 ? '+' : '−'}{money(estateDelta)} vs {baseWord}</p>
                )}
            </div>
        </div>
        {figures.length > 0 && (
            <div className={`grid grid-cols-1 divide-y divide-slate-200 border-y border-slate-200 bg-slate-50/70 sm:divide-x sm:divide-y-0 ${figures.length >= 3 ? 'sm:grid-cols-3' : figures.length === 2 ? 'sm:grid-cols-2' : ''}`}>
                {figures.map((f) => (
                    <div key={f.label} className="px-6 py-3.5" data-testid={f.testId}>
                        <p className="text-[12px] font-semibold text-slate-500">{f.label}</p>
                        <p className={`mt-0.5 text-[20px] font-bold leading-none tabular-nums ${f.tone === 'good' ? 'text-emerald-700' : 'text-slate-900'}`}>{f.value}{f.unit ? <span className="ml-1 text-[12px] font-medium text-slate-500">{f.unit}</span> : null}</p>
                        {f.note && <p className="mt-1 text-[12px] text-slate-500">{f.note}</p>}
                    </div>
                ))}
            </div>
        )}
        <div className="px-6 py-4">
            {children}
            <div className={`${children ? 'mt-4 ' : ''}grid grid-cols-1 gap-2 sm:grid-cols-3`}>
                <button type="button" onClick={onExplain} className="inline-flex items-center justify-center gap-1.5 rounded-lg border border-slate-300 bg-white px-3.5 py-2.5 text-[14px] font-semibold text-slate-800 hover:border-blue-300 hover:bg-blue-50" data-testid={`${testIdPrefix}-explain`}><BookOpen size={15} /> Explain this</button>
                <button type="button" onClick={onRisks} className="inline-flex items-center justify-center gap-1.5 rounded-lg border border-slate-300 bg-white px-3.5 py-2.5 text-[14px] font-semibold text-slate-800 hover:border-blue-300 hover:bg-blue-50" data-testid={`${testIdPrefix}-risks`}><ShieldAlert size={15} /> What could go wrong</button>
                <button type="button" onClick={onAllPlans} className="inline-flex items-center justify-center gap-1.5 rounded-lg bg-blue-600 hover:bg-blue-700 px-3.5 py-2.5 text-[14px] font-semibold text-white shadow-md " data-testid={`${testIdPrefix}-all-plans`}><Sparkles size={15} /> All the plans we tested →</button>
            </div>
        </div>
    </div>
);

export interface DocumentAndNextStepProps {
    /** "draft", "prepared", "not prepared yet". */
    documentState: string;
    documentLine: React.ReactNode;
    /** The document's link or button. */
    documentAction: React.ReactNode;
    nextTitle: string;
    nextLine: React.ReactNode;
    /** "Full action plan on Home". */
    nextAction: React.ReactNode;
}

export const DocumentAndNextStep = ({ documentState, documentLine, documentAction, nextTitle, nextLine, nextAction }: DocumentAndNextStepProps) => (
    <div className="grid grid-cols-1 divide-y divide-slate-200 rounded-2xl border border-slate-200 bg-white shadow-card md:grid-cols-2 md:divide-x md:divide-y-0">
        <div className="flex gap-3 px-5 py-4">
            <span className="grid h-9 w-9 shrink-0 place-items-center rounded-lg bg-blue-50 text-blue-700"><FileText size={18} /></span>
            <div className="min-w-0">
                <p className="text-[15px] font-semibold text-slate-900">Advice document — {documentState}</p>
                <p className="mt-0.5 text-[13px] leading-relaxed text-slate-600">{documentLine} {documentAction}.</p>
            </div>
        </div>
        <div className="flex gap-3 px-5 py-4">
            <span className="grid h-9 w-9 shrink-0 place-items-center rounded-lg bg-blue-600 text-[14px] font-bold text-white">1</span>
            <div className="min-w-0">
                <p className="text-[15px] font-semibold text-slate-900">Next: {nextTitle}</p>
                <p className="mt-0.5 text-[13px] leading-relaxed text-slate-600">{nextLine} {nextAction}.</p>
            </div>
        </div>
    </div>
);

export interface ModelOption { title: string; body: string; action: string; onClick: () => void; testId?: string }

export const ModelSomethingElse = ({ options, testId = 'model-something-else' }: { options: ModelOption[]; testId?: string }) => (
    <div className="rounded-2xl border border-slate-200 bg-white px-5 py-4 shadow-card" data-testid={testId}>
        <p className="text-[15px] font-semibold text-slate-900">Model something else</p>
        <p className="mt-0.5 text-[13px] text-slate-600">Each new model is saved as its own plan. This plan stays chosen until you choose another.</p>
        <div className={`mt-3 grid grid-cols-1 gap-3 ${options.length > 1 ? 'sm:grid-cols-2' : ''}`}>
            {options.map((o) => (
                <div key={o.title} className="flex flex-col rounded-xl border border-slate-200 px-4 py-3">
                    <p className="text-[14px] font-semibold text-slate-900">{o.title}</p>
                    <p className="mt-0.5 flex-1 text-[13px] leading-relaxed text-slate-600">{o.body}</p>
                    <button type="button" onClick={o.onClick} data-testid={o.testId} className="mt-3 inline-flex items-center justify-center gap-1.5 self-start rounded-lg border border-slate-300 bg-white px-3.5 py-2 text-[13px] font-semibold text-slate-800 hover:border-blue-300 hover:bg-blue-50">{o.action}</button>
                </div>
            ))}
        </div>
    </div>
);

export interface PlanPageLink { label: string; icon: React.ReactNode; onClick: () => void; testId?: string }

export const PlanPageLinks = ({ links }: { links: PlanPageLink[] }) => (
    <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
        {links.map((l) => (
            <button key={l.label} type="button" onClick={l.onClick} data-testid={l.testId} className="inline-flex items-center gap-1.5 text-[13px] font-semibold text-blue-700 underline decoration-dotted underline-offset-2 hover:text-blue-900">
                {l.icon}{l.label}
            </button>
        ))}
    </div>
);
